<template>
  <div
    class="FormInput"
    :class="{
      'FormInput--hasIcon': !!iconName,
    }"
  >
    <div
      v-if="prefix"
      class="FormInput__prefix"
    >
      {{ prefix }}
    </div>
    <Input
      ref="inputRef"
      v-model="modelValue"
      class="FormInput__input"
      :inputmode="inputMode"
      :aria-label="ariaLabel"
      :maxlength="maxLength"
      v-bind="{
        name,
        type,
        placeholder,
        autocomplete,
        autocapitalize,
        spellcheck,
        autocorrect,
        is1PasswordEnabled,
        isRequired,
        isDisabled,
        shouldAutoFocus,
        isMultiline,
        enterkeyhint,
        rows,
      }"
      @focus="haptic(ImpactStyle.Light)"
    />
    <div
      v-if="iconName"
      class="FormInput__iconContainer"
    >
      <Icon
        class="FormInput__icon"
        :name="iconName"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { IconName } from "@/components/Icon.types";
import Icon from "@/components/Icon.vue";
import Input from "@/components/Input.vue";
import haptic from "@/lib/utils/platform/haptic";
import { ImpactStyle } from "@capacitor/haptics";
import { ComponentPublicInstance, ref } from "vue";

const modelValue = defineModel<string | number | null>({
  required: true,
});

const {
  textAlign = "left",
} = defineProps<{
  name: string,
  type?: string,
  placeholder?: string,
  autocomplete?: string,
  autocapitalize?: string,
  spellcheck?: boolean,
  autocorrect?: string,
  isRequired?: boolean,
  isDisabled?: boolean,
  inputMode?: string,
  iconName?: keyof typeof IconName,
  is1PasswordEnabled?: boolean,
  ariaLabel?: string,
  shouldAutoFocus?: boolean,
  isMultiline?: boolean,
  enterkeyhint?: string,
  rows?: number,
  textAlign?: "left" | "center" | "right",
  maxLength?: number,
  prefix?: string,
}>();

const inputRef = ref<ComponentPublicInstance<typeof Input> | null>(null);

defineExpose({
  focus: () => inputRef.value?.$el?.focus(),
});
</script>

<style lang="scss" scoped>
.FormInput {
  --inputHeight: 48px;
  --iconWidth: 0px;
  --highlightWidth: 3px;
  --outlineWidth: 2px;
  --offsetWidth: calc(var(--highlightWidth) + var(--outlineWidth));
  --paddingInline: 12px;
  --borderRadius: calc(6px + var(--offsetWidth));
  --outlineColorDisabled: var(--themeDarkTransparent500);
  --backgroundColorDisabled: var(--themeLightTransparent600);
  --textColorDisabled: var(--themeDarkTransparent500);
  --placeholderColorDisabled: var(--themeDarkTransparent400);
  --arrowColorDisabled: var(--themeColorNeutral500);
  --highlightColorDisabled: var(--themeLightTransparent400);
  --buttonBackgroundColorDisabled: var(--themeColorNeutral150);
  --transitionDuration: 350ms;

  position: relative;

  display: flex;
  align-items: center;

  border-radius: var(--borderRadius);
  background-color: var(--themeColorInputBackground);
  color: var(--themeColorInputForeground);
  box-shadow: 0 0 0 var(--highlightWidth) transparent inset;

  text-align: v-bind(textAlign);

  transition: var(--transitionDuration);
  transition-property: color, background-color, box-shadow;

  &.FormInput--hasIcon {
    --iconWidth: var(--inputHeight);
  }

  &:has([type="search"]) {
    --borderRadius: 9999px;
    --paddingInline: 16px;
  }

  &::after {
    position: absolute;
    inset: 0;

    border-radius: inherit;
    outline: var(--outlineWidth) solid var(--themeColorNeutral900);
    outline-offset: calc(-1 * var(--offsetWidth));

    transition: outline-color var(--transitionDuration);

    pointer-events: none;
    content: "";
  }

  [data-theme="light"] & {
    --highlightWidth: 0px;
    --outlineColorDisabled: var(--themeColorNeutral400);
    box-shadow: 0 0 0 var(--highlightWidth) transparent;
  }

  &:has(:disabled) {
    background-color: var(--backgroundColorDisabled);
    color: var(--textColorDisabled);

    &::after {
      outline-color: var(--outlineColorDisabled);
    }
  }

  &:focus-within {
    box-shadow: 0 0 0 var(--highlightWidth) var(--themeColorSecondary) inset;

    [data-theme="light"] & {
      box-shadow: 0 0 0 3px var(--themeColorSecondary);
    }
  }
}

.FormInput__prefix {
  padding-left: calc(var(--paddingInline) + var(--offsetWidth));
  color: var(--themeColorNeutral500);
}

.FormInput__input {
  display: flex;
  align-items: center;
  padding: 0 calc(var(--paddingInline) + var(--iconWidth) + var(--offsetWidth)) 0 calc(var(--paddingInline) + var(--offsetWidth));

  text-align: inherit;

  &:is(input) {
    height: calc(var(--inputHeight) + 2 * var(--highlightWidth));
    white-space: nowrap;
  }

  &:is(textarea) {
    padding-block: calc(10px + var(--fontSafariOffset)) 11px;
    resize: none;
    line-height: 1.5;
  }

  &::-webkit-search-cancel-button {
    position: relative;
    right: -6px;

    width: 24px;
    height: 24px;
    margin-inline: 0;

    border: solid 2px var(--themeColorNeutral900);
    border-radius: 50%;
    background: url("data:image/svg+xml,<svg width='10' height='10' viewBox='0 0 10 10' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M9.70087 8.29412C9.95331 8.54656 10.0519 8.9145 9.9595 9.25934C9.8671 9.60418 9.59775 9.87353 9.25291 9.96593C8.90807 10.0583 8.54013 9.95974 8.28769 9.70729L4.99677 6.41636L1.70585 9.70729C1.31562 10.0975 0.682918 10.0975 0.29268 9.7073C-0.097558 9.31706 -0.0975603 8.68436 0.292675 8.29412L3.5836 5.00318L0.292675 1.71224C0.0402476 1.45979 -0.0583231 1.09185 0.0340933 0.747009C0.12651 0.402172 0.395873 0.132834 0.740717 0.0404515C1.08556 -0.0519313 1.45349 0.0466764 1.70592 0.29913L4.99684 3.59007L8.28776 0.29913C8.53942 0.0428764 8.90923 -0.0586129 9.25643 0.0332965C9.60363 0.125206 9.87479 0.396374 9.9667 0.743574C10.0586 1.09077 9.95712 1.46059 9.70087 1.71224L6.40995 5.00318L9.70087 8.29412Z' fill='black'/></svg>");
    background-color: var(--themeColorNeutral100);
    background-position: center;
    background-size: 10px;
    background-repeat: no-repeat;
    appearance: none;

    transition: var(--transitionDuration);
    transition-property: opacity, background-color;
    cursor: pointer;
  }

  [data-os="android"] .FormInput--hasIcon & {
    padding-right: calc(var(--inputHeight) + var(--offsetWidth));
  }

  .FormInput:has(.FormInput__prefix) & {
    padding-left: 0.125em;
  }

  &:disabled::placeholder {
    color: var(--placeholderColorDisabled);
  }

  &:disabled::-webkit-search-cancel-button {
    background-color: transparent;
    opacity: 0.4;
  }
}

.FormInput__iconContainer {
  --iconSize: 20px;
  position: absolute;
  inset: var(--highlightWidth) var(--highlightWidth) var(--highlightWidth) auto;

  display: grid;
  place-items: center;
  width: calc(var(--iconWidth) - var(--outlineWidth));
  height: var(--inputHeight);
  aspect-ratio: 1;
  padding-right: var(--outlineWidth);

  border-radius: 0 var(--borderRadius) var(--borderRadius) 0;
  background-color: var(--themeColorSecondary);
  color: var(--themeColorSecondaryForeground);

  transition: var(--transitionDuration) background-color;

  input:disabled + & {
    background-color: var(--buttonBackgroundColorDisabled);
    color: var(--arrowColorDisabled);
  }
}

.FormInput__icon {
  transition: var(--transitionDuration) color;
}
</style>
